/**
 * Build-time application configuration, sourced from Vite's `import.meta.env`
 * (`VITE_*` variables — see `.env.example`). These are baked into the bundle
 * at build time, so never put secrets here; they are visible in client-side
 * JavaScript.
 */

export interface AppConfig {
  readonly environment: string;
  readonly apiBaseUrl: string;
  readonly enableMockApi: boolean;
  readonly logLevel: 'debug' | 'info' | 'warn' | 'error';
}

const LOG_LEVELS: ReadonlyArray<AppConfig['logLevel']> = [
  'debug',
  'info',
  'warn',
  'error',
];

let cachedConfig: AppConfig | undefined;

export class AppConfigError extends Error {}

/** Reads and validates config from `import.meta.env`. */
export function getAppConfig(): AppConfig {
  // Return the cached configuration if it has already been resolved.
  if (cachedConfig) {
    return cachedConfig;
  }

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

  if (!apiBaseUrl) {
    throw new AppConfigError(
      'VITE_API_BASE_URL is required (see .env.example).',
    );
  }

  const logLevel = import.meta.env.VITE_LOG_LEVEL ?? 'info';

  if (!LOG_LEVELS.includes(logLevel as AppConfig['logLevel'])) {
    throw new AppConfigError(
      `VITE_LOG_LEVEL must be one of: ${LOG_LEVELS.join(', ')}.`,
    );
  }

  cachedConfig = {
    environment: import.meta.env.MODE,
    apiBaseUrl,
    enableMockApi: import.meta.env.VITE_ENABLE_MOCK_API === 'true',
    logLevel: logLevel as AppConfig['logLevel'],
  };

  return cachedConfig;
}
